import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  BarChart3,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  Coins,
  Eye,
  Gauge,
  Globe2,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MousePointerClick,
  PanelLeftClose,
  RefreshCw,
  Search,
  SearchCheck,
  Settings2,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UserCog,
  Users,
} from "lucide-react";
import {
  FormEvent,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./auth/AuthContext";
import { AdminPanel } from "./components/AdminPanel";
import { ConversionsView } from "./components/ConversionsView";
import { DateRangePicker } from "./components/DateRangePicker";
import { EmptyState } from "./components/EmptyState";
import {
  MetricBarChart,
  type MetricBarPoint,
} from "./components/MetricBarChart";
import { MetricCard } from "./components/MetricCard";
import { MetricTrendChart } from "./components/MetricTrendChart";
import { MyDevicesPanel } from "./components/MyDevicesPanel";
import { ScoreRing } from "./components/ScoreRing";
import { SourceRail } from "./components/SourceRail";
import {
  LANGUAGE_OPTIONS,
  useI18n,
  type LanguageCode,
} from "./i18n";
import { fetchDashboard, fetchSources } from "./lib/api";
import { createInitialDateFilters } from "./shared/date-ranges";
import type {
  CountryFilterOption,
  DashboardRequest,
  DashboardFilters,
  DashboardResponse,
  Ga4SourceMediumRow,
  Ga4SourceMediumSeriesPoint,
  OrganicBreakdownKey,
  OrganicBreakdownRow,
  QueryRow,
  SourceStatus,
} from "./shared/contracts";
import { getCountryDisplayName } from "./shared/countries";

const PerformanceChart = lazy(() =>
  import("./components/PerformanceChart").then((module) => ({
    default: module.PerformanceChart,
  })),
);

const today = new Date();
const initialDateFilters = createInitialDateFilters(undefined, today);

const initialFilters: DashboardFilters = {
  urlMode: "exact",
  queryMode: "contains",
  country: "",
  device: "all",
  ...initialDateFilters,
};

interface CountrySelectOption {
  code: string;
  label: string;
  clicks: number | null;
  impressions: number | null;
}

function dashboardRequestSignature(url: string, filters: DashboardFilters) {
  return JSON.stringify({ url, filters });
}

const URL_HISTORY_STORAGE_KEY = "arenatours.analytics.recent-urls";

function readRecentUrls() {
  if (typeof window === "undefined") return [];
  try {
    const value = JSON.parse(
      window.localStorage.getItem(URL_HISTORY_STORAGE_KEY) || "[]",
    );
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
}

function rememberRecentUrl(value: string) {
  const url = value.trim();
  if (!url || typeof window === "undefined") return;
  const next = [
    url,
    ...readRecentUrls().filter((item) => item !== url),
  ].slice(0, 8);
  window.localStorage.setItem(URL_HISTORY_STORAGE_KEY, JSON.stringify(next));
}

function buildCountryOptions(
  countries: CountryFilterOption[] | undefined,
  locale: string,
): CountrySelectOption[] {
  const byCode = new Map<string, CountrySelectOption>();
  for (const country of countries || []) {
    const code = country.code.trim().toUpperCase();
    if (!code) continue;
    const current = byCode.get(code);
    byCode.set(code, {
      code,
      label: getCountryDisplayName(code, locale),
      clicks: (current?.clicks || 0) + (country.clicks || 0),
      impressions:
        (current?.impressions || 0) + (country.impressions || 0),
    });
  }
  return [...byCode.values()].sort((a, b) =>
    a.label.localeCompare(b.label, locale),
  );
}

function filterCountryOptions(
  countries: CountrySelectOption[],
  search: string,
) {
  const query = normalizeFilterText(search);
  if (!query) return countries;
  return countries.filter((country) =>
    [country.label, country.code].some((value) =>
      normalizeFilterText(value).includes(query),
    ),
  );
}

function normalizeFilterText(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

type ViewKey =
  | "overview"
  | "organic"
  | "paid"
  | "conversions"
  | "technical"
  | "devices"
  | "users";

type I18nText = ReturnType<typeof useI18n>["text"];

const ORGANIC_BREAKDOWN_TABS: OrganicBreakdownKey[] = [
  "queries",
  "countries",
  "devices",
  "searchAppearance",
  "days",
];

export default function App() {
  const { user, logout } = useAuth();
  const { text, locale } = useI18n();
  const [urlInput, setUrlInput] = useState("https://arenatours.com/");
  const [analyzedUrl, setAnalyzedUrl] = useState(urlInput);
  const [filters, setFilters] = useState(initialFilters);
  const [activeView, setActiveView] = useState<ViewKey>("overview");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [urlHistory, setUrlHistory] = useState<string[]>(readRecentUrls);
  const [urlMenuOpen, setUrlMenuOpen] = useState(false);
  const [countryInput, setCountryInput] = useState("");
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const lastAppliedRequest = useRef("");
  const queryClient = useQueryClient();
  const viewLabels = useMemo<Record<ViewKey, string>>(
    () => ({
      overview: text.nav.overview,
      organic: text.nav.organic,
      paid: text.nav.paid,
      conversions: text.nav.conversions,
      technical: text.nav.technical,
      devices: text.nav.devices,
      users: text.nav.users,
    }),
    [text],
  );
  const fallbackSources = useMemo<SourceStatus[]>(
    () => [
      {
        key: "technical",
        label: text.sources.labels.technical,
        state: "connected",
        detail: text.technical.liveInspection,
      },
      {
        key: "pagespeed",
        label: text.sources.labels.pagespeed,
        state: "connected",
        detail: "Lighthouse + Core Web Vitals",
      },
    ],
    [text],
  );

  const sourcesQuery = useQuery({
    queryKey: ["sources"],
    queryFn: fetchSources,
    staleTime: 5 * 60 * 1000,
  });

  const dashboardMutation = useMutation({
    mutationFn: async (request: DashboardRequest) => {
      const queryKey = [
        "dashboard",
        dashboardRequestSignature(request.url, request.filters),
      ];
      return queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchDashboard(request),
        staleTime: request.forceRefresh ? 0 : 15 * 60 * 1000,
      });
    },
    onSuccess: (result) => {
      const url = result.url.normalized;
      rememberRecentUrl(url);
      setUrlHistory(readRecentUrls());
    },
  });

  const dashboard = dashboardMutation.data;
  const sources = dashboard?.sources || sourcesQuery.data?.sources || fallbackSources;
  const countryOptions = useMemo(
    () => buildCountryOptions(dashboard?.filterOptions?.countries, locale),
    [dashboard?.filterOptions?.countries, locale],
  );
  const visibleCountryOptions = useMemo(
    () => filterCountryOptions(countryOptions, countryInput).slice(0, 10),
    [countryOptions, countryInput],
  );
  const visibleUrlHistory = useMemo(() => {
    const query = normalizeFilterText(urlInput);
    return urlHistory
      .filter((url) => !query || normalizeFilterText(url).includes(query))
      .slice(0, 6);
  }, [urlHistory, urlInput]);
  const filterSignature = useMemo(() => JSON.stringify(filters), [filters]);
  const hasDashboard = Boolean(dashboard);

  useEffect(() => {
    if (!filters.country) return;
    const option = countryOptions.find(
      (country) => country.code === filters.country,
    );
    setCountryInput(option?.label || getCountryDisplayName(filters.country, locale));
  }, [countryOptions, filters.country, locale]);

  useEffect(() => {
    if (!hasDashboard || dashboardMutation.isPending) return;
    const requestSignature = dashboardRequestSignature(analyzedUrl, filters);
    if (lastAppliedRequest.current === requestSignature) return;

    const timeout = window.setTimeout(() => {
      lastAppliedRequest.current = requestSignature;
      dashboardMutation.mutate({
        url: analyzedUrl,
        filters,
      });
    }, 650);

    return () => window.clearTimeout(timeout);
  }, [
    analyzedUrl,
    dashboardMutation,
    dashboardMutation.isPending,
    filterSignature,
    filters,
    hasDashboard,
  ]);

  function runDashboard(nextFilters: DashboardFilters, forceRefresh = false) {
    const targetUrl = urlInput;
    setAnalyzedUrl(targetUrl);
    lastAppliedRequest.current = dashboardRequestSignature(
      targetUrl,
      nextFilters,
    );
    dashboardMutation.mutate({
      url: targetUrl,
      filters: nextFilters,
      forceRefresh,
    });
  }

  function submit(event: FormEvent, forceRefresh = false) {
    event.preventDefault();
    runDashboard(filters, forceRefresh);
  }

  function applyDateFilters(nextFilters: DashboardFilters) {
    setFilters(nextFilters);
    if (!dashboard) runDashboard(nextFilters);
  }

  function selectView(view: ViewKey) {
    setActiveView(view);
    setMobileNavOpen(false);
  }

  function selectCountry(country: CountrySelectOption) {
    setCountryInput(country.label);
    setCountryMenuOpen(false);
    setFilters((current) => ({
      ...current,
      country: country.code,
    }));
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNavOpen ? "open" : ""}`}>
        <div className="brand">
          <div className="brand-mark">A</div>
          <div>
            <strong>Arenatours</strong>
            <span>Analytics 360</span>
          </div>
        </div>

        <nav className="primary-nav" aria-label={text.filters.openNavigation}>
          <NavButton
            icon={LayoutDashboard}
            label={viewLabels.overview}
            active={activeView === "overview"}
            onClick={() => selectView("overview")}
          />
          <NavButton
            icon={SearchCheck}
            label={viewLabels.organic}
            active={activeView === "organic"}
            onClick={() => selectView("organic")}
          />
          <NavButton
            icon={CircleDollarSign}
            label={viewLabels.paid}
            active={activeView === "paid"}
            onClick={() => selectView("paid")}
          />
          <NavButton
            icon={ShieldCheck}
            label={viewLabels.technical}
            active={activeView === "technical"}
            onClick={() => selectView("technical")}
          />
          <NavButton
            icon={Target}
            label={viewLabels.conversions}
            active={activeView === "conversions"}
            onClick={() => selectView("conversions")}
          />
          <NavButton
            icon={KeyRound}
            label={viewLabels.devices}
            active={activeView === "devices"}
            onClick={() => selectView("devices")}
          />
          {user.role === "admin" ? (
            <NavButton
              icon={UserCog}
              label={viewLabels.users}
              active={activeView === "users"}
              onClick={() => selectView("users")}
            />
          ) : null}
        </nav>

        <SourceRail sources={sources} />

        <LanguageSwitcher />

        <div className="sidebar-foot">
          <Settings2 size={16} />
          <span>{user.displayName}</span>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <button
            className="icon-button mobile-nav-button"
            onClick={() => setMobileNavOpen((value) => !value)}
            title={text.filters.openNavigation}
          >
            <PanelLeftClose size={19} />
          </button>
          <div>
            <span className="eyebrow">{text.dashboard.eyebrow}</span>
            <h1>{viewLabels[activeView]}</h1>
          </div>
          <div className="topbar-status">
            <span className="live-dot" />
            <span>{user.email}</span>
            <button
              className="icon-button"
              title={text.filters.closeSession}
              onClick={logout}
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {activeView === "users" && user.role === "admin" ? (
          <AdminPanel currentUser={user} />
        ) : activeView === "devices" ? (
          <MyDevicesPanel currentUser={user} />
        ) : (
          <>
        <section className="control-band">
          <div className="url-search-shell">
            <form className="url-search" onSubmit={submit}>
              <Search size={19} />
              <input
                aria-label={text.filters.urlToAnalyze}
                aria-expanded={urlMenuOpen}
                aria-controls="url-history-options"
                value={urlInput}
                onFocus={() => setUrlMenuOpen(true)}
                onBlur={() =>
                  window.setTimeout(() => setUrlMenuOpen(false), 120)
                }
                onChange={(event) => {
                  setUrlInput(event.target.value);
                  setUrlMenuOpen(true);
                }}
                placeholder="https://arenatours.com/landing-page/"
              />
              <button
                className="primary-button"
                type="submit"
                disabled={dashboardMutation.isPending}
              >
                {dashboardMutation.isPending ? (
                  <RefreshCw className="spin" size={17} />
                ) : (
                  <Activity size={17} />
                )}
                {text.filters.analyze}
              </button>
            </form>
            {urlMenuOpen && visibleUrlHistory.length ? (
              <div className="url-history-menu" id="url-history-options">
                <div className="url-history-heading">
                  <Clock3 size={14} />
                  <span>{text.filters.recentSearches}</span>
                </div>
                {visibleUrlHistory.map((url) => (
                  <button
                    key={url}
                    type="button"
                    className="url-history-option"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setUrlInput(url);
                      setUrlMenuOpen(false);
                    }}
                  >
                    <Clock3 size={15} />
                    <span>{url}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="filter-row">
            <SelectControl
              icon={Target}
              label={text.filters.urlMode}
              value={filters.urlMode}
              onChange={(value) =>
                setFilters((current) => ({
                  ...current,
                  urlMode: value as DashboardFilters["urlMode"],
                }))
              }
              options={[
                ["exact", text.filters.exactUrl, "EXACT"],
                ["contains", text.filters.urlContaining, "IN"],
                ["not_contains", text.filters.urlNotContaining, "NOT"],
              ]}
            />
            <label className="filter-input">
              <Search size={15} />
              <input
                placeholder={text.filters.queryPlaceholder}
                value={filters.query || ""}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    query: event.target.value,
                  }))
                }
              />
            </label>
            <label className="filter-input country-filter">
              <Globe2 size={15} />
              <div className="country-combobox">
                <input
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={countryMenuOpen}
                  aria-controls="country-filter-options"
                  placeholder={text.filters.countryPlaceholder}
                  value={countryInput}
                  onFocus={() => setCountryMenuOpen(true)}
                  onBlur={() =>
                    window.setTimeout(() => setCountryMenuOpen(false), 120)
                  }
                  onChange={(event) => {
                    const value = event.target.value;
                    setCountryInput(value);
                    setCountryMenuOpen(true);
                    setFilters((current) =>
                      current.country
                        ? { ...current, country: "" }
                        : current,
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && visibleCountryOptions[0]) {
                      event.preventDefault();
                      selectCountry(visibleCountryOptions[0]);
                    }
                    if (event.key === "Escape") {
                      setCountryMenuOpen(false);
                    }
                  }}
                />
                {countryMenuOpen && visibleCountryOptions.length ? (
                  <div
                    className="country-options"
                    id="country-filter-options"
                    role="listbox"
                  >
                    {visibleCountryOptions.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        role="option"
                        aria-selected={filters.country === country.code}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectCountry(country)}
                      >
                        <span>{country.label}</span>
                        <small>{country.code}</small>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </label>
            <SelectControl
              icon={Gauge}
              label={text.filters.device}
              value={filters.device || "all"}
              onChange={(value) =>
                setFilters((current) => ({
                  ...current,
                  device: value as DashboardFilters["device"],
                }))
              }
              options={[
                ["all", text.filters.allDevices, "ALL"],
                ["mobile", text.filters.mobile, "MOB"],
                ["desktop", text.filters.desktop, "DESK"],
                ["tablet", text.filters.tablet, "TAB"],
              ]}
            />
            <DateRangePicker
              value={filters}
              disabled={dashboardMutation.isPending}
              onApply={applyDateFilters}
            />
          </div>
        </section>

        {dashboardMutation.isError ? (
          <div className="error-banner">
              <CircleDollarSign size={18} />
              <div>
              <strong>{text.dashboard.analysisErrorTitle}</strong>
              <span>{dashboardMutation.error.message}</span>
            </div>
          </div>
        ) : null}

        {!dashboard && !dashboardMutation.isPending ? (
          <section className="first-run">
            <EmptyState
              title={text.dashboard.firstRunTitle}
              body={text.dashboard.firstRunBody}
            />
          </section>
        ) : null}

        {dashboardMutation.isPending ? <DashboardSkeleton /> : null}

        {dashboard ? (
          <DashboardContentNew
            dashboard={dashboard}
            activeView={activeView}
            onRefresh={(event) => submit(event, true)}
          />
        ) : null}
          </>
        )}
      </main>
    </div>
  );
}

function NavButton({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: typeof LayoutDashboard;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const { text, locale } = useI18n();

  return (
    <button className={active ? "active" : ""} onClick={onClick}>
      <Icon size={17} />
      <span>{label}</span>
    </button>
  );
}

function LanguageSwitcher() {
  const { language, setLanguage, text } = useI18n();

  return (
    <label className="language-switcher">
      <span>{text.language.label}</span>
      <select
        value={language}
        aria-label={text.language.label}
        onChange={(event) => setLanguage(event.target.value as LanguageCode)}
      >
        {LANGUAGE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} />
    </label>
  );
}

function SelectControl({
  icon: Icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: typeof Target;
  label: string;
  value: string;
  options: Array<[string, string, string?]>;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const controlRef = useRef<HTMLDivElement>(null);
  const selected = options.find(([optionValue]) => optionValue === value);
  const listboxId = `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-options`;

  useEffect(() => {
    if (!open) return;
    function closeOnOutsideClick(event: MouseEvent) {
      if (!controlRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [open]);

  function selectOption(optionValue: string) {
    onChange(optionValue);
    setOpen(false);
  }

  return (
    <div className={`select-control ${open ? "open" : ""}`} ref={controlRef}>
      <Icon size={15} />
      <span className="sr-only">{label}</span>
      <button
        type="button"
        className="select-trigger"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
          if (event.key === "ArrowDown" || event.key === "Enter") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span>{selected?.[1] || value}</span>
        <ChevronDown className="select-chevron" size={14} />
      </button>
      {open ? (
        <div className="select-options" id={listboxId} role="listbox">
          {options.map(([optionValue, optionLabel, optionMeta]) => (
            <button
              key={optionValue}
              type="button"
              role="option"
              aria-selected={optionValue === value}
              onClick={() => selectOption(optionValue)}
            >
              <span>{optionLabel}</span>
              {optionMeta ? <small>{optionMeta}</small> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DashboardContentNew({
  dashboard,
  activeView,
  onRefresh,
}: {
  dashboard: DashboardResponse;
  activeView: ViewKey;
  onRefresh: (event: FormEvent) => void;
}) {
  const { text, locale } = useI18n();

  return (
    <div className="dashboard-content">
      <section className="url-identity">
        <div>
          <span className="url-host">
            {new URL(dashboard.url.normalized).host}
          </span>
          <strong>{dashboard.url.normalized}</strong>
          <span>
            {dashboard.url.isHome
              ? text.dashboard.siteWideView
              : text.dashboard.landingPage}
            {" · "}
            {text.dashboard.updated}{" "}
            {new Intl.DateTimeFormat(locale, {
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(dashboard.generatedAt))}
          </span>
          {dashboard.url.dataUrl !== dashboard.url.normalized ? (
            <span className="data-url">
              {text.dashboard.dataUrl} {dashboard.url.dataUrl}
            </span>
          ) : null}
        </div>
        <button className="secondary-button" onClick={onRefresh}>
          <RefreshCw size={15} />
          {text.dashboard.refreshSources}
        </button>
      </section>

      {dashboard.warnings.length ? (
        <div className="warning-strip">
          {dashboard.warnings.map((warning) => (
            <span key={warning}>{warning}</span>
          ))}
        </div>
      ) : null}

      {activeView === "overview" ? <OverviewView dashboard={dashboard} /> : null}
      {activeView === "organic" ? <OrganicView dashboard={dashboard} /> : null}
      {activeView === "paid" ? <PaidView dashboard={dashboard} /> : null}
      {activeView === "conversions" ? (
        <ConversionsView dashboard={dashboard} />
      ) : null}
      {activeView === "technical" ? (
        <TechnicalView dashboard={dashboard} />
      ) : null}
    </div>
  );
}

function OverviewView({ dashboard }: { dashboard: DashboardResponse }) {
  const { text, locale } = useI18n();
  const ga4 = dashboard.ga4;
  const organic = dashboard.searchConsole;
  const paid = dashboard.googleAds;
  const bing = dashboard.bing;
  const clarity = dashboard.clarity;
  const microsoftPaid = dashboard.microsoftAds;
  const sourceMediumRows = getTopSourceMediumRows(
    ga4?.firstUserSourceMedium?.rows,
    8,
  );
  const sourceMediumChart = buildSourceMediumChart(
    ga4?.firstUserSourceMedium?.series,
    sourceMediumRows.slice(0, 5),
  );
  const hasSourceMediumPrevious = sourceMediumRows.some((row) =>
    Boolean(row.previous),
  );
  const claritySignals: MetricBarPoint[] = [
    {
      label: text.overview.scrollDepth,
      value: clarity?.scrollDepth ?? NaN,
      color: "#087a5a",
    },
    {
      label: text.overview.deadClicks,
      value: clarity?.deadClickSessionsPercent ?? NaN,
      color: "#b56f09",
    },
    {
      label: text.overview.rageClicks,
      value: clarity?.rageClickSessionsPercent ?? NaN,
      color: "#b7473e",
    },
    {
      label: text.overview.quickbacks,
      value: clarity?.quickbackSessionsPercent ?? NaN,
      color: "#176bb1",
    },
    {
      label: text.overview.scriptErrors,
      value: clarity?.scriptErrorSessionsPercent ?? NaN,
      color: "#6f5d91",
    },
  ].filter((point) => Number.isFinite(point.value));

  return (
    <>
      <section className="metric-grid">
        <MetricCard
          label={text.dashboard.urlHealth}
          metric={dashboard.summary.healthScore}
          icon={ShieldCheck}
          tone="green"
        />
        <MetricCard
          label={text.dashboard.sessions}
          metric={dashboard.summary.sessions}
          icon={Users}
          tone="ink"
        />
        <MetricCard
          label={text.dashboard.organicClicks}
          metric={dashboard.summary.organicClicks}
          icon={MousePointerClick}
          tone="blue"
        />
        <MetricCard
          label={text.dashboard.paidClicks}
          metric={dashboard.summary.paidClicks}
          icon={CircleDollarSign}
          tone="amber"
        />
        <MetricCard
          label={text.dashboard.conversions}
          metric={dashboard.summary.conversions}
          icon={Target}
          tone="red"
        />
        <MetricCard
          label={text.dashboard.performance}
          metric={dashboard.summary.performance}
          icon={Gauge}
          tone="green"
        />
      </section>
      <section className="view-grid">
        <article className="panel span-2 chart-panel">
          <PanelHeading
            title={text.overview.trafficConversions}
            meta="Google Analytics 4"
          />
          {ga4?.series.length ? (
            <MetricTrendChart
              points={ga4.series}
              comparePoints={ga4.compareSeries}
              compareLabel={formatCompareLabel(dashboard, text)}
              compareAlignBy={dashboard.filters.compareAlignBy}
              xKey="date"
              series={[
                {
                  key: "sessions",
                  label: text.dashboard.sessions,
                  color: "#087a5a",
                  area: true,
                },
                {
                  key: "users",
                  label: text.admin.users,
                  color: "#176bb1",
                },
                {
                  key: "conversions",
                  label: text.dashboard.conversions,
                  color: "#b7473e",
                  axis: 1,
                },
              ]}
            />
          ) : (
            <SourceEmpty title={text.overview.noGa4Activity} />
          )}
        </article>
        <article className="panel">
          <PanelHeading title={text.overview.acquisitionChannels} meta="GA4" />
          {ga4?.channels.length ? (
            <div className="compact-list">
              {ga4.channels.slice(0, 8).map((channel) => (
                <div key={channel.channel}>
                  <span>{channel.channel}</span>
                  <strong>{formatNumber(channel.sessions, locale)}</strong>
                  <small>
                    {formatNumber(channel.conversions, locale)} conv.
                  </small>
                </div>
              ))}
            </div>
          ) : (
            <SourceEmpty title={text.overview.noChannels} />
          )}
        </article>
        <article className="panel span-2 chart-panel">
          <PanelHeading
            title={text.overview.firstUserSourceMediumTitle}
            meta={text.overview.firstUserSourceMediumMeta}
          />
          {sourceMediumChart.points.length && sourceMediumChart.series.length ? (
            <MetricTrendChart
              points={sourceMediumChart.points}
              xKey="date"
              series={sourceMediumChart.series}
            />
          ) : (
            <SourceEmpty title={text.overview.noFirstUserSourceMedium} />
          )}
        </article>
        <article className="panel source-medium-panel">
          <PanelHeading
            title={text.overview.firstUserSourceMediumTable}
            meta={text.overview.firstUserSourceMediumMeta}
          />
          {sourceMediumRows.length ? (
            <div className="query-table-wrap">
              <table className="query-table source-medium-table">
                <thead>
                  <tr>
                    <th>{text.overview.sourceMedium}</th>
                    <th>{text.dashboard.sessions}</th>
                    <th>{text.dashboard.conversions}</th>
                    <th>{text.overview.conversionRate}</th>
                    {hasSourceMediumPrevious ? (
                      <th>{text.overview.previousConversions}</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {sourceMediumRows.map((row) => (
                    <tr key={row.sourceMedium}>
                      <td>{row.sourceMedium}</td>
                      <td>{formatNumber(row.sessions, locale)}</td>
                      <td>{formatNumber(row.conversions, locale)}</td>
                      <td>{formatPercent(row.conversionRate)}</td>
                      {hasSourceMediumPrevious ? (
                        <td>
                          {row.previous
                            ? formatNumber(row.previous.conversions, locale)
                            : "—"}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <SourceEmpty title={text.overview.noFirstUserSourceMedium} />
          )}
        </article>
      </section>

      <section className="view-grid overview-channel-grid">
        <OverviewSearchSummary
          google={organic}
          bing={bing}
          locale={locale}
        />
        <OverviewPaidSummary
          google={paid}
          microsoft={microsoftPaid}
          locale={locale}
        />
        <article className="panel chart-panel">
          <PanelHeading
            title={text.overview.landingBehavior}
            meta={
              clarity
                ? `Clarity · ${clarity.periodDays * 24} h · ${formatNumber(clarity.sessions, locale)} ${text.dashboard.sessions.toLowerCase()}`
                : "Microsoft Clarity"
            }
          />
          {claritySignals.length ? (
            <MetricBarChart points={claritySignals} unit="percent" />
          ) : (
            <SourceEmpty title={text.overview.noClaritySignals} />
          )}
        </article>
      </section>

    </>
  );
}

function OverviewSearchSummary({
  google,
  bing,
  locale,
}: {
  google: DashboardResponse["searchConsole"];
  bing: DashboardResponse["bing"];
  locale: string;
}) {
  const { text } = useI18n();
  const rows = [
    {
      platform: "Google",
      source: "Search Console",
      clicks: google?.clicks ?? null,
      impressions: google?.impressions ?? null,
      ctr: google?.ctr ?? null,
      position: google?.averagePosition ?? null,
    },
    {
      platform: "Bing",
      source: "Bing Webmaster",
      clicks: bing?.clicks ?? null,
      impressions: bing?.impressions ?? null,
      ctr: bing?.ctr ?? null,
      position: bing?.averagePosition ?? null,
    },
  ].filter((row) =>
    [row.clicks, row.impressions, row.ctr, row.position].some(
      (value) => value !== null && value !== undefined,
    ),
  );

  return (
    <article className="panel overview-summary-panel">
      <PanelHeading
        title={text.overview.searchVisibilitySummary}
        meta="Google Search Console + Bing Webmaster"
      />
      {rows.length ? (
        <div className="overview-comparison-table-wrap">
          <table className="overview-comparison-table">
            <thead>
              <tr>
                <th>{text.organic.source}</th>
                <th>{text.organic.clicks}</th>
                <th>{text.organic.impr}</th>
                <th>CTR</th>
                <th>{text.organic.pos}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.platform}>
                  <td>
                    <strong>{row.platform}</strong>
                    <small>{row.source}</small>
                  </td>
                  <td>{formatNumber(row.clicks, locale)}</td>
                  <td>{formatNumber(row.impressions, locale)}</td>
                  <td>{formatPercent(row.ctr)}</td>
                  <td>
                    {row.position === null || row.position === undefined
                      ? "-"
                      : row.position.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <SourceEmpty title={text.overview.noSearchVisibilitySummary} />
      )}
    </article>
  );
}

function OverviewPaidSummary({
  google,
  microsoft,
  locale,
}: {
  google: DashboardResponse["googleAds"];
  microsoft: DashboardResponse["microsoftAds"];
  locale: string;
}) {
  const { text } = useI18n();
  const rows = [
    {
      platform: "Google Ads",
      clicks: google?.clicks ?? null,
      cost: google?.cost ?? null,
      conversions: google?.conversions ?? null,
      cpa: google?.cpa ?? null,
    },
    {
      platform: "Microsoft Ads",
      clicks: microsoft?.clicks ?? null,
      cost: microsoft?.cost ?? null,
      conversions: microsoft?.conversions ?? null,
      cpa: microsoft?.cpa ?? null,
    },
  ].filter((row) =>
    [row.clicks, row.cost, row.conversions, row.cpa].some(
      (value) => value !== null && value !== undefined,
    ),
  );

  return (
    <article className="panel overview-summary-panel">
      <PanelHeading
        title={text.overview.paidMediaSummary}
        meta="Google Ads + Microsoft Advertising"
      />
      {rows.length ? (
        <div className="overview-comparison-table-wrap">
          <table className="overview-comparison-table">
            <thead>
              <tr>
                <th>{text.organic.source}</th>
                <th>{text.organic.clicks}</th>
                <th>{text.paid.cost}</th>
                <th>Conv.</th>
                <th>CPA</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.platform}>
                  <td>
                    <strong>{row.platform}</strong>
                    <small>{text.dashboard.paidClicks}</small>
                  </td>
                  <td>{formatNumber(row.clicks, locale)}</td>
                  <td>{formatCurrency(row.cost, locale)}</td>
                  <td>{formatNumber(row.conversions, locale)}</td>
                  <td>{formatCurrency(row.cpa, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <SourceEmpty title={text.overview.noPaidMediaSummary} />
      )}
    </article>
  );
}

const SOURCE_MEDIUM_COLORS = [
  "#087a5a",
  "#176bb1",
  "#b7473e",
  "#b56f09",
  "#6f5d91",
];

function getTopSourceMediumRows(
  rows: Ga4SourceMediumRow[] | undefined,
  limit: number,
) {
  return [...(rows || [])]
    .sort(
      (a, b) =>
        b.conversions - a.conversions ||
        b.sessions - a.sessions ||
        a.sourceMedium.localeCompare(b.sourceMedium),
    )
    .slice(0, limit);
}

function buildSourceMediumChart(
  points: Ga4SourceMediumSeriesPoint[] | undefined,
  rows: Ga4SourceMediumRow[],
) {
  const trackedSources = rows.map((row, index) => ({
    sourceMedium: row.sourceMedium,
    key: `source_${index}`,
    color: SOURCE_MEDIUM_COLORS[index % SOURCE_MEDIUM_COLORS.length],
  }));
  const keyBySource = new Map(
    trackedSources.map((source) => [source.sourceMedium, source.key]),
  );
  const dates = new Map<string, Record<string, string | number>>();

  for (const point of points || []) {
    const key = keyBySource.get(point.sourceMedium);
    if (!key) continue;
    const current =
      dates.get(point.date) ||
      Object.fromEntries([
        ["date", point.date],
        ...trackedSources.map((source) => [source.key, 0]),
      ]);
    current[key] = Number(current[key] || 0) + point.conversions;
    dates.set(point.date, current);
  }

  return {
    points: [...dates.values()].sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    ),
    series: trackedSources.map((source, index) => ({
      key: source.key,
      label: source.sourceMedium,
      color: source.color,
      area: index === 0,
    })),
  };
}

function OrganicView({ dashboard }: { dashboard: DashboardResponse }) {
  const { text, locale } = useI18n();
  const organic = dashboard.searchConsole;
  const bing = dashboard.bing;
  return (
    <>
      <section className="metric-grid">
        <MetricCard
          label={text.organic.googleClicks}
          metric={{
            value: organic?.clicks ?? null,
            previous: organic?.previous?.clicks,
          }}
          icon={MousePointerClick}
          tone="blue"
        />
        <MetricCard
          label={text.organic.impressions}
          metric={{
            value: organic?.impressions ?? null,
            previous: organic?.previous?.impressions,
          }}
          icon={Eye}
          tone="ink"
        />
        <MetricCard
          label="CTR"
          metric={{
            value: organic?.ctr === null || organic?.ctr === undefined
              ? null
              : organic.ctr * 100,
            previous:
              organic?.previous?.ctr === null ||
              organic?.previous?.ctr === undefined
                ? undefined
                : organic.previous.ctr * 100,
            unit: "percent",
          }}
          icon={TrendingUp}
          tone="green"
        />
        <MetricCard
          label={text.organic.averagePosition}
          metric={{
            value: organic?.averagePosition ?? null,
            previous: organic?.previous?.averagePosition,
            unit: "position",
          }}
          icon={SearchCheck}
          tone="amber"
        />
        <MetricCard
          label={text.organic.googleQueries}
          metric={{ value: organic?.queries.length ?? null }}
          icon={Search}
          tone="ink"
        />
      </section>
      <section className="view-grid">
        <article className="panel span-2 chart-panel">
          <PanelHeading
            title={text.organic.organicPerformance}
            meta="Google Search Console"
          />
          {organic?.series.length ? (
            <MetricTrendChart
              points={organic.series}
              comparePoints={organic.compareSeries}
              compareLabel={formatCompareLabel(dashboard, text)}
              compareAlignBy={dashboard.filters.compareAlignBy}
              xKey="date"
              series={[
                {
                  key: "clicks",
                  label: text.organic.clicks,
                  color: "#176bb1",
                  area: true,
                },
                {
                  key: "impressions",
                  label: text.organic.impressions,
                  color: "#087a5a",
                  axis: 1,
                },
              ]}
            />
          ) : (
            <SourceEmpty title={text.organic.noGscDailySeries} />
          )}
        </article>
        <article className="panel">
          <PanelHeading title={text.organic.indexation} meta="URL Inspection" />
          <div className="technical-table">
            <TechnicalRow
              label={text.organic.verdict}
              value={organic?.indexation?.verdict || "—"}
              good={organic?.indexation?.verdict === "PASS"}
            />
            <TechnicalRow
              label={text.organic.coverage}
              value={organic?.indexation?.coverageState || "—"}
              good={organic?.indexation?.indexingState !== "BLOCKED_BY_META_TAG"}
            />
            <TechnicalRow
              label="Robots"
              value={organic?.indexation?.robotsTxtState || "—"}
              good={organic?.indexation?.robotsTxtState === "ALLOWED"}
            />
            <TechnicalRow
              label={text.organic.lastCrawl}
              value={formatDateTime(organic?.indexation?.lastCrawlTime, locale)}
              good={Boolean(organic?.indexation?.lastCrawlTime)}
            />
            <TechnicalRow
              label={text.organic.inspectedUrl}
              value={organic?.indexation?.inspectedUrl || "—"}
              good={
                organic?.indexation?.inspectedUrl === dashboard.url.dataUrl
              }
            />
          </div>
        </article>
        <OrganicBreakdownPanel
          title={text.organic.organicQueries}
          meta="Google Search Console"
          source="google"
          breakdowns={organic?.breakdowns}
          fallbackQueries={organic?.queries}
        />
      </section>

      <EcosystemHeading
        title={text.organic.microsoftOrganicSearch}
        meta={text.organic.microsoftOrganicMeta}
      />
      <section className="metric-grid microsoft-metric-grid">
        <MetricCard
          label={text.organic.bingClicks}
          metric={{
            value: bing?.clicks ?? null,
            previous: bing?.previous?.clicks,
          }}
          icon={MousePointerClick}
          tone="blue"
        />
        <MetricCard
          label={text.organic.bingImpressions}
          metric={{
            value: bing?.impressions ?? null,
            previous: bing?.previous?.impressions,
          }}
          icon={Eye}
          tone="ink"
        />
        <MetricCard
          label={text.organic.bingCtr}
          metric={{
            value:
              bing?.ctr === null || bing?.ctr === undefined
                ? null
                : bing.ctr * 100,
            previous:
              bing?.previous?.ctr === null ||
              bing?.previous?.ctr === undefined
                ? undefined
                : bing.previous.ctr * 100,
            unit: "percent",
          }}
          icon={TrendingUp}
          tone="green"
        />
        <MetricCard
          label={text.organic.bingPosition}
          metric={{
            value: bing?.averagePosition ?? null,
            previous: bing?.previous?.averagePosition,
            unit: "position",
          }}
          icon={SearchCheck}
          tone="amber"
        />
        <MetricCard
          label={text.organic.bingQueries}
          metric={{ value: bing?.queries.length ?? null }}
          icon={Search}
          tone="ink"
        />
        <MetricCard
          label={text.organic.inboundAnchors}
          metric={{ value: bing?.urlInfo?.anchorCount ?? null }}
          icon={Globe2}
          tone="blue"
        />
      </section>
      <section className="view-grid microsoft-grid">
        <article className="panel span-2 chart-panel">
          <PanelHeading
            title={text.organic.bingPerformance}
            meta={text.organic.bingMeta}
          />
          {bing?.series.length ? (
            <MetricTrendChart
              points={bing.series}
              comparePoints={bing.compareSeries}
              compareLabel={formatCompareLabel(dashboard, text)}
              compareAlignBy={dashboard.filters.compareAlignBy}
              xKey="date"
              series={[
                {
                  key: "clicks",
                  label: text.organic.clicks,
                  color: "#176bb1",
                  area: true,
                },
                {
                  key: "impressions",
                  label: text.organic.impressions,
                  color: "#087a5a",
                  axis: 1,
                },
              ]}
            />
          ) : (
            <SourceEmpty title={text.organic.noBingDailySeries} />
          )}
        </article>
        <article className="panel">
          <PanelHeading title={text.organic.bingStatus} meta="URL index details" />
          <div className="technical-table">
            <TechnicalRow
              label={text.organic.recognized}
              value={
                bing?.urlInfo?.isPage
                  ? text.organic.pageRecognized
                  : text.organic.notRecognized
              }
              good={Boolean(bing?.urlInfo?.isPage)}
            />
            <TechnicalRow
              label={text.organic.lastCrawl}
              value={formatDateTime(bing?.urlInfo?.lastCrawledDate, locale)}
              good={Boolean(bing?.urlInfo?.lastCrawledDate)}
            />
            <TechnicalRow
              label={text.organic.discovered}
              value={formatDateTime(bing?.urlInfo?.discoveryDate, locale)}
              good={Boolean(bing?.urlInfo?.discoveryDate)}
            />
            <TechnicalRow
              label={text.organic.document}
              value={formatBytes(bing?.urlInfo?.documentSize)}
              good={(bing?.urlInfo?.documentSize || 0) > 0}
            />
            <TechnicalRow
              label={text.organic.inspectedUrl}
              value={bing?.urlInfo?.inspectedUrl || "—"}
              good={bing?.urlInfo?.inspectedUrl === dashboard.url.dataUrl}
            />
          </div>
        </article>
        <OrganicBreakdownPanel
          title={text.organic.organicQueries}
          meta="Bing Webmaster"
          source="bing"
          breakdowns={bing?.breakdowns}
          fallbackQueries={bing?.queries}
        />
      </section>
    </>
  );
}

function OrganicBreakdownPanel({
  title,
  meta,
  source,
  breakdowns,
  fallbackQueries,
}: {
  title: string;
  meta: string;
  source: OrganicBreakdownRow["source"];
  breakdowns?: Partial<Record<OrganicBreakdownKey, OrganicBreakdownRow[]>>;
  fallbackQueries?: QueryRow[];
}) {
  const { text, locale } = useI18n();
  const [activeTab, setActiveTab] =
    useState<OrganicBreakdownKey>("queries");
  const hasNativeTab = Boolean(
    breakdowns && Object.prototype.hasOwnProperty.call(breakdowns, activeTab),
  );
  const hasFallbackQueries =
    activeTab === "queries" && Boolean(fallbackQueries?.length);
  const isAvailable = hasNativeTab || hasFallbackQueries;
  const rows = (
    breakdowns?.[activeTab] ||
    (activeTab === "queries" ? queryRowsToBreakdown(fallbackQueries) : [])
  ).slice(0, 20);

  return (
    <article className="panel span-3 query-panel">
      <PanelHeading title={title} meta={meta} />
      <div className="query-tabs" role="tablist" aria-label={title}>
        {ORGANIC_BREAKDOWN_TABS.map((tab) => {
          const tabRows =
            breakdowns?.[tab] ||
            (tab === "queries" ? queryRowsToBreakdown(fallbackQueries) : []);
          const tabAvailable = Boolean(
            breakdowns && Object.prototype.hasOwnProperty.call(breakdowns, tab),
          ) || (tab === "queries" && Boolean(fallbackQueries?.length));
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={activeTab === tab ? "active" : ""}
              onClick={() => setActiveTab(tab)}
            >
              <span>{text.organic.tabs[tab]}</span>
              {tabAvailable ? <small>{tabRows.length}</small> : null}
            </button>
          );
        })}
      </div>

      {isAvailable && rows.length ? (
        <div className="query-table-wrap">
          <table className="query-table">
            <thead>
              <tr>
                <th>{breakdownHeader(activeTab, text)}</th>
                <th>{text.organic.source}</th>
                <th>{text.organic.clicks}</th>
                <th>{text.organic.impr}</th>
                <th>CTR</th>
                <th>{text.organic.pos}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${source}-${activeTab}-${row.value}`}>
                  <td>{formatBreakdownValue(row.value, activeTab, text, locale)}</td>
                  <td>{row.source}</td>
                  <td>{formatNumber(row.clicks, locale)}</td>
                  <td>{formatNumber(row.impressions, locale)}</td>
                  <td>{formatPercent(row.ctr)}</td>
                  <td>
                    {row.position === null || row.position === undefined
                      ? "—"
                      : row.position.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <SourceEmpty
          title={
            isAvailable
              ? activeTab === "queries"
                ? text.organic.noQueries
                : text.organic.noBreakdownRows
              : text.organic.noBreakdownAvailable
          }
        />
      )}
    </article>
  );
}

function queryRowsToBreakdown(rows?: QueryRow[]): OrganicBreakdownRow[] {
  return (rows || []).map((row) => ({
    value: row.query,
    clicks: row.clicks,
    impressions: row.impressions,
    ctr: row.ctr,
    position: row.position,
    source: row.source,
  }));
}

function breakdownHeader(tab: OrganicBreakdownKey, text: I18nText) {
  if (tab === "countries") return text.organic.country;
  if (tab === "devices") return text.organic.device;
  if (tab === "searchAppearance") return text.organic.searchAppearance;
  if (tab === "days") return text.organic.day;
  return text.organic.query;
}

function formatBreakdownValue(
  value: string,
  tab: OrganicBreakdownKey,
  text: I18nText,
  locale: string,
) {
  if (!value) return "—";
  if (tab === "countries") return getCountryDisplayName(value, locale);
  if (tab === "devices") return formatOrganicDevice(value, text);
  if (tab === "searchAppearance") return formatSearchAppearance(value);
  if (tab === "days") return formatDateTime(value, locale);
  return value;
}

function formatOrganicDevice(value: string, text: I18nText) {
  const normalized = value.trim().toUpperCase();
  if (normalized === "MOBILE") return text.filters.mobile;
  if (normalized === "DESKTOP") return text.filters.desktop;
  if (normalized === "TABLET") return text.filters.tablet;
  return value;
}

function formatSearchAppearance(value: string) {
  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function PaidView({ dashboard }: { dashboard: DashboardResponse }) {
  const { text, locale } = useI18n();
  const paid = dashboard.googleAds;
  const microsoftPaid = dashboard.microsoftAds;
  const microsoftStatus = dashboard.sources.find(
    (source) => source.key === "microsoft_ads",
  );
  return (
    <>
      <section className="metric-grid">
        <MetricCard
          label={text.dashboard.paidClicks}
          metric={{
            value: paid?.clicks ?? null,
            previous: paid?.previous?.clicks,
          }}
          icon={MousePointerClick}
          tone="amber"
        />
        <MetricCard
          label={text.paid.impressions}
          metric={{
            value: paid?.impressions ?? null,
            previous: paid?.previous?.impressions,
          }}
          icon={Eye}
          tone="ink"
        />
        <MetricCard
          label={text.paid.cost}
          metric={{
            value: paid?.cost ?? null,
            previous: paid?.previous?.cost,
            unit: "currency",
          }}
          icon={Coins}
          tone="red"
        />
        <MetricCard
          label={text.dashboard.conversions}
          metric={{
            value: paid?.conversions ?? null,
            previous: paid?.previous?.conversions,
          }}
          icon={Target}
          tone="green"
        />
        <MetricCard
          label="CPA"
          metric={{ value: paid?.cpa ?? null, unit: "currency" }}
          icon={CircleDollarSign}
          tone="amber"
        />
        <MetricCard
          label="ROAS"
          metric={{ value: paid?.roas ?? null, unit: "ratio" }}
          icon={TrendingUp}
          tone="blue"
        />
      </section>
      <section className="view-grid">
        <article className="panel span-2 chart-panel">
          <PanelHeading title={text.paid.investmentResults} meta="Google Ads" />
          {paid?.series.length ? (
            <MetricTrendChart
              points={paid.series}
              comparePoints={paid.compareSeries}
              compareLabel={formatCompareLabel(dashboard, text)}
              compareAlignBy={dashboard.filters.compareAlignBy}
              xKey="date"
              series={[
                {
                  key: "cost",
                  label: text.paid.cost,
                  color: "#b7473e",
                  area: true,
                },
                {
                  key: "clicks",
                  label: text.organic.clicks,
                  color: "#176bb1",
                  axis: 1,
                },
                {
                  key: "conversions",
                  label: text.dashboard.conversions,
                  color: "#087a5a",
                  axis: 1,
                },
              ]}
            />
          ) : (
            <SourceEmpty title={text.paid.noLinkedCampaigns} />
          )}
        </article>
        <article className="panel">
          <PanelHeading title={text.paid.efficiency} meta="Google Ads" />
          <div className="technical-table">
            <TechnicalRow
              label="CTR"
              value={formatPercent(paid?.ctr)}
              good={(paid?.ctr || 0) > 0}
            />
            <TechnicalRow
              label={text.paid.avgCpc}
              value={formatCurrency(paid?.averageCpc, locale)}
              good={paid?.averageCpc !== null && paid?.averageCpc !== undefined}
            />
            <TechnicalRow
              label={text.paid.convValue}
              value={formatCurrency(paid?.conversionValue, locale)}
              good={(paid?.conversionValue || 0) > 0}
            />
            <TechnicalRow
              label={text.paid.campaigns}
              value={String(paid?.campaigns.length || 0)}
              good={Boolean(paid?.campaigns.length)}
            />
          </div>
        </article>
        <article className="panel span-3 query-panel">
          <PanelHeading title={text.paid.landingCampaigns} meta="Google Ads" />
          {paid?.campaigns.length ? (
            <div className="query-table-wrap">
              <table className="query-table campaign-table">
                <thead>
                  <tr>
                    <th>{text.paid.campaign}</th>
                    <th>{text.organic.clicks}</th>
                    <th>{text.organic.impr}</th>
                    <th>{text.paid.cost}</th>
                    <th>Conv.</th>
                    <th>CPA</th>
                    <th>ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {paid.campaigns.slice(0, 20).map((campaign) => (
                    <tr key={campaign.id}>
                      <td>{campaign.name}</td>
                      <td>{formatNumber(campaign.clicks, locale)}</td>
                      <td>{formatNumber(campaign.impressions, locale)}</td>
                      <td>{formatCurrency(campaign.cost, locale)}</td>
                      <td>{campaign.conversions.toFixed(1)}</td>
                      <td>{formatCurrency(campaign.cpa, locale)}</td>
                      <td>{campaign.roas?.toFixed(2) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <SourceEmpty title={text.paid.noPaidActivity} />
          )}
        </article>
        <article className="panel span-3 query-panel">
          <PanelHeading
            title={text.paid.linkedCampaignCountries}
            meta={`Google Ads · ${text.paid.linkedCampaignCountriesMeta}`}
          />
          {paid?.countryAttribution?.rows.length ? (
            <>
              <p className="panel-note">
                {text.paid.countryAttributionHelp}
                {paid.countryAttribution.urlShare !== null
                  ? ` ${text.paid.urlShare}: ${formatPercent(paid.countryAttribution.urlShare)}.`
                  : ""}
              </p>
              <div className="query-table-wrap">
                <table className="query-table campaign-country-table">
                  <thead>
                    <tr>
                      <th>{text.organic.country}</th>
                      <th>{text.organic.clicks}</th>
                      <th>{text.organic.impr}</th>
                      <th>{text.paid.cost}</th>
                      <th>Conv.</th>
                      <th>{text.paid.urlShare}</th>
                      <th>{text.paid.confidence}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paid.countryAttribution.rows.slice(0, 20).map((row) => (
                      <tr key={row.countryCriterionId}>
                        <td>
                          {row.countryCode
                            ? getCountryDisplayName(row.countryCode, locale)
                            : row.countryName}
                        </td>
                        <td>{formatNumber(row.clicks, locale)}</td>
                        <td>{formatNumber(row.impressions, locale)}</td>
                        <td>{formatCurrency(row.cost, locale)}</td>
                        <td>{row.conversions.toFixed(1)}</td>
                        <td>{formatPercent(row.urlShare)}</td>
                        <td>
                          <span className={`confidence-badge ${row.confidence}`}>
                            {text.paid.confidenceLabels[row.confidence]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <SourceEmpty title={text.paid.noCountryAttribution} />
          )}
        </article>
      </section>

      <EcosystemHeading
        title={text.paid.microsoftPaidSearch}
        meta={text.paid.microsoftPaidMeta}
      />
      {microsoftPaid ? (
        <>
          <section className="metric-grid microsoft-metric-grid">
            <MetricCard
              label={text.paid.microsoftClicks}
              metric={{
                value: microsoftPaid.clicks,
                previous: microsoftPaid.previous?.clicks,
              }}
              icon={MousePointerClick}
              tone="blue"
            />
            <MetricCard
              label={text.paid.impressions}
              metric={{
                value: microsoftPaid.impressions,
                previous: microsoftPaid.previous?.impressions,
              }}
              icon={Eye}
              tone="ink"
            />
            <MetricCard
              label={text.paid.cost}
              metric={{
                value: microsoftPaid.cost,
                previous: microsoftPaid.previous?.cost,
                unit: "currency",
              }}
              icon={Coins}
              tone="red"
            />
            <MetricCard
              label={text.dashboard.conversions}
              metric={{
                value: microsoftPaid.conversions,
                previous: microsoftPaid.previous?.conversions,
              }}
              icon={Target}
              tone="green"
            />
            <MetricCard
              label="CPA"
              metric={{ value: microsoftPaid.cpa, unit: "currency" }}
              icon={CircleDollarSign}
              tone="amber"
            />
            <MetricCard
              label="ROAS"
              metric={{ value: microsoftPaid.roas, unit: "ratio" }}
              icon={TrendingUp}
              tone="blue"
            />
          </section>
          <section className="view-grid microsoft-grid">
            <article className="panel span-2 chart-panel">
              <PanelHeading
                title={text.paid.investmentResults}
                meta="Microsoft Advertising"
              />
              {microsoftPaid.series.length ? (
                <MetricTrendChart
                  points={microsoftPaid.series}
                  comparePoints={microsoftPaid.compareSeries}
                  compareLabel={formatCompareLabel(dashboard, text)}
                  compareAlignBy={dashboard.filters.compareAlignBy}
                  xKey="date"
                  series={[
                    {
                      key: "cost",
                      label: text.paid.cost,
                      color: "#b7473e",
                      area: true,
                    },
                    {
                      key: "clicks",
                      label: text.organic.clicks,
                      color: "#176bb1",
                      axis: 1,
                    },
                    {
                      key: "conversions",
                      label: text.dashboard.conversions,
                      color: "#087a5a",
                      axis: 1,
                    },
                  ]}
                />
              ) : (
                <SourceEmpty title={text.paid.noMicrosoftAdsActivity} />
              )}
            </article>
            <article className="panel">
              <PanelHeading title={text.paid.efficiency} meta="Microsoft Advertising" />
              <div className="technical-table">
                <TechnicalRow
                  label="CTR"
                  value={formatPercent(microsoftPaid.ctr)}
                  good={(microsoftPaid.ctr || 0) > 0}
                />
                <TechnicalRow
                  label={text.paid.avgCpc}
                  value={formatCurrency(microsoftPaid.averageCpc, locale)}
                  good={microsoftPaid.averageCpc !== null}
                />
                <TechnicalRow
                  label={text.paid.convValue}
                  value={formatCurrency(microsoftPaid.conversionValue, locale)}
                  good={microsoftPaid.conversionValue > 0}
                />
                <TechnicalRow
                  label={text.paid.campaigns}
                  value={String(microsoftPaid.campaigns.length)}
                  good={microsoftPaid.campaigns.length > 0}
                />
              </div>
            </article>
            <article className="panel span-3 query-panel">
              <PanelHeading
                title={text.paid.landingCampaigns}
                meta="Microsoft Advertising"
              />
              {microsoftPaid.campaigns.length ? (
                <div className="query-table-wrap">
                  <table className="query-table campaign-table">
                    <thead>
                      <tr>
                        <th>{text.paid.campaign}</th>
                        <th>{text.organic.clicks}</th>
                        <th>{text.organic.impr}</th>
                        <th>{text.paid.cost}</th>
                        <th>Conv.</th>
                        <th>CPA</th>
                        <th>ROAS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {microsoftPaid.campaigns.slice(0, 20).map((campaign) => (
                        <tr key={campaign.id}>
                          <td>{campaign.name}</td>
                          <td>{formatNumber(campaign.clicks, locale)}</td>
                          <td>{formatNumber(campaign.impressions, locale)}</td>
                          <td>{formatCurrency(campaign.cost, locale)}</td>
                          <td>{campaign.conversions.toFixed(1)}</td>
                          <td>{formatCurrency(campaign.cpa, locale)}</td>
                          <td>{campaign.roas?.toFixed(2) || "â€”"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <SourceEmpty title={text.paid.noMicrosoftAdsActivity} />
              )}
            </article>
            <article className="panel span-3 query-panel">
              <PanelHeading
                title={text.paid.linkedCampaignCountries}
                meta={`Microsoft Advertising · ${text.paid.linkedCampaignCountriesMeta}`}
              />
              {microsoftPaid.countryAttribution?.rows.length ? (
                <>
                  <p className="panel-note">
                    {text.paid.countryAttributionHelp}
                    {microsoftPaid.countryAttribution.urlShare !== null
                      ? ` ${text.paid.urlShare}: ${formatPercent(microsoftPaid.countryAttribution.urlShare)}.`
                      : ""}
                  </p>
                  <div className="query-table-wrap">
                    <table className="query-table campaign-country-table">
                      <thead>
                        <tr>
                          <th>{text.organic.country}</th>
                          <th>{text.organic.clicks}</th>
                          <th>{text.organic.impr}</th>
                          <th>{text.paid.cost}</th>
                          <th>Conv.</th>
                          <th>{text.paid.urlShare}</th>
                          <th>{text.paid.confidence}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {microsoftPaid.countryAttribution.rows
                          .slice(0, 20)
                          .map((row) => (
                            <tr key={row.countryCriterionId}>
                              <td>
                                {row.countryCode
                                  ? getCountryDisplayName(row.countryCode, locale)
                                  : row.countryName}
                              </td>
                              <td>{formatNumber(row.clicks, locale)}</td>
                              <td>{formatNumber(row.impressions, locale)}</td>
                              <td>{formatCurrency(row.cost, locale)}</td>
                              <td>{row.conversions.toFixed(1)}</td>
                              <td>{formatPercent(row.urlShare)}</td>
                              <td>
                                <span
                                  className={`confidence-badge ${row.confidence}`}
                                >
                                  {text.paid.confidenceLabels[row.confidence]}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <SourceEmpty title={text.paid.noCountryAttribution} />
              )}
            </article>
          </section>
        </>
      ) : (
        <section className="panel microsoft-pending-panel">
          <PanelHeading
            title="Microsoft Advertising"
            meta={
              microsoftStatus?.state === "error"
                ? text.paid.microsoftPendingMetaError
                : text.paid.microsoftPendingMetaOauth
            }
          />
          <SourceEmpty
            title={
              microsoftStatus?.detail ||
              text.paid.microsoftPendingTitle
            }
          />
        </section>
      )}
    </>
  );
}

function TechnicalView({ dashboard }: { dashboard: DashboardResponse }) {
  const { text, locale } = useI18n();
  const scores = dashboard.pageSpeed?.scores;
  return (
    <>
      <section className="metric-grid">
        <MetricCard
          label={text.dashboard.urlHealth}
          metric={dashboard.summary.healthScore}
          icon={ShieldCheck}
          tone="green"
        />
        <MetricCard
          label={text.dashboard.performance}
          metric={dashboard.summary.performance}
          icon={Gauge}
          tone="green"
        />
        <MetricCard
          label={text.technical.seoScore}
          metric={{ value: scores?.seo ?? null, unit: "score" }}
          icon={SearchCheck}
          tone="blue"
        />
        <MetricCard
          label="CrUX LCP"
          metric={{
            value: dashboard.crux?.current.lcpMs ?? null,
            unit: "milliseconds",
          }}
          icon={Gauge}
          tone="amber"
        />
        <MetricCard
          label="CrUX INP"
          metric={{
            value: dashboard.crux?.current.inpMs ?? null,
            unit: "milliseconds",
          }}
          icon={Activity}
          tone="red"
        />
        <MetricCard
          label="CrUX CLS"
          metric={{ value: dashboard.crux?.current.cls ?? null, unit: "ratio" }}
          icon={TrendingUp}
          tone="ink"
        />
      </section>
      <section className="view-grid">
        <article className="panel span-2 chart-panel">
          <PanelHeading
            title={text.technical.historicalCoreWebVitals}
            meta={`CrUX ${dashboard.crux?.scope || "URL"} · ${
              text.technical.lastSixMonths
            }`}
          />
          {dashboard.crux?.history.length ? (
            <MetricTrendChart
              points={dashboard.crux.history}
              xKey="endDate"
              series={[
                {
                  key: "lcpMs",
                  label: "LCP ms",
                  color: "#087a5a",
                  area: true,
                },
                {
                  key: "inpMs",
                  label: "INP ms",
                  color: "#b7473e",
                },
                {
                  key: "ttfbMs",
                  label: "TTFB ms",
                  color: "#176bb1",
                },
              ]}
            />
          ) : (
            <SourceEmpty title={text.technical.noCruxHistory} />
          )}
        </article>
        <article className="panel score-panel">
          <PanelHeading title="Lighthouse" meta="Mobile" />
          <div className="score-grid">
            <ScoreRing
              label={text.dashboard.performance}
              value={scores?.performance ?? null}
            />
            <ScoreRing
              label={text.technical.accessibility}
              value={scores?.accessibility ?? null}
            />
            <ScoreRing
              label={text.technical.bestPractices}
              value={scores?.bestPractices ?? null}
            />
            <ScoreRing label="SEO" value={scores?.seo ?? null} />
          </div>
        </article>
        <article className="panel technical-panel">
          <PanelHeading
            title={text.technical.technicalSeo}
            meta={text.technical.liveInspection}
          />
          <div className="technical-table">
            <TechnicalRow
              label={text.technical.httpStatus}
              value={dashboard.technical?.statusCode?.toString() || "—"}
              good={(dashboard.technical?.statusCode || 500) < 400}
            />
            <TechnicalRow
              label="Canonical"
              value={
                dashboard.technical?.canonical ||
                text.technical.canonicalNotDetected
              }
              good={Boolean(dashboard.technical?.canonical)}
            />
            <TechnicalRow
              label="Robots"
              value={
                dashboard.technical?.robots ||
                text.technical.robotsNoRestrictions
              }
              good={
                !dashboard.technical?.robots?.toLowerCase().includes("noindex")
              }
            />
            <TechnicalRow
              label="Schema"
              value={
                dashboard.technical?.schemaTypes.length
                  ? dashboard.technical.schemaTypes.join(", ")
                  : text.technical.schemaNotDetected
              }
              good={
                Boolean(dashboard.technical?.schemaTypes.length) &&
                !dashboard.technical?.schemaIssues.length
              }
            />
            <TechnicalRow
              label={text.technical.responseTime}
              value={formatMilliseconds(dashboard.technical?.responseTimeMs)}
              good={(dashboard.technical?.responseTimeMs || 9999) < 1000}
            />
          </div>
        </article>
        <article className="panel span-2 bing-technical-panel">
          <PanelHeading
            title={text.technical.microsoftCrawlIndex}
            meta={text.technical.noPageSpeedEquivalent}
          />
          <div className="bing-signal-grid">
            <div className="technical-table">
              <TechnicalRow
                label={text.organic.recognized}
                value={
                  dashboard.bing?.urlInfo?.isPage
                    ? text.organic.pageRecognized
                    : text.organic.notRecognized
                }
                good={Boolean(dashboard.bing?.urlInfo?.isPage)}
              />
              <TechnicalRow
                label={text.organic.lastCrawl}
                value={formatDateTime(
                  dashboard.bing?.urlInfo?.lastCrawledDate,
                  locale,
                )}
                good={Boolean(dashboard.bing?.urlInfo?.lastCrawledDate)}
              />
              <TechnicalRow
                label={text.organic.discovered}
                value={formatDateTime(
                  dashboard.bing?.urlInfo?.discoveryDate,
                  locale,
                )}
                good={Boolean(dashboard.bing?.urlInfo?.discoveryDate)}
              />
            </div>
            <div className="technical-table">
              <TechnicalRow
                label={text.organic.document}
                value={formatBytes(dashboard.bing?.urlInfo?.documentSize)}
                good={(dashboard.bing?.urlInfo?.documentSize || 0) > 0}
              />
              <TechnicalRow
                label={text.technical.anchors}
                value={formatNumber(
                  dashboard.bing?.urlInfo?.anchorCount ?? null,
                  locale,
                )}
                good={(dashboard.bing?.urlInfo?.anchorCount || 0) > 0}
              />
              <TechnicalRow
                label={text.organic.inspectedUrl}
                value={dashboard.bing?.urlInfo?.inspectedUrl || "—"}
                good={
                  dashboard.bing?.urlInfo?.inspectedUrl === dashboard.url.dataUrl
                }
              />
            </div>
          </div>
        </article>
        <article className="panel span-2 chart-panel">
          <PanelHeading
            title={text.technical.auditHistory}
            meta={`${dashboard.history.length} ${text.technical.snapshots}`}
          />
          {dashboard.history.length ? (
            <Suspense fallback={<div className="chart-loading" />}>
              <PerformanceChart history={dashboard.history} />
            </Suspense>
          ) : (
            <SourceEmpty title={text.technical.futureAnalyses} />
          )}
        </article>
        <article className="panel opportunities-panel">
          <PanelHeading title={text.technical.priorityActions} meta="PageSpeed" />
          <div className="opportunity-list">
            {dashboard.pageSpeed?.opportunities.length ? (
              dashboard.pageSpeed.opportunities.slice(0, 6).map((item, index) => (
                <div className="opportunity-row" key={item.title}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {item.displayValue || text.technical.reviewOpportunity}
                    </small>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-actions">
                <Sparkles size={18} />
                {text.technical.noCriticalOpportunities}
              </div>
            )}
          </div>
        </article>
      </section>
    </>
  );
}

function SourceEmpty({ title }: { title: string }) {
  return (
    <div className="source-empty">
      <BarChart3 size={19} />
      <span>{title}</span>
    </div>
  );
}

function DashboardContent({
  dashboard,
  activeView,
  visibleQueries,
  onRefresh,
}: {
  dashboard: DashboardResponse;
  activeView: ViewKey;
  visibleQueries: NonNullable<DashboardResponse["bing"]>["queries"];
  onRefresh: (event: FormEvent) => void;
}) {
  const scores = dashboard.pageSpeed?.scores;

  return (
    <div className="dashboard-content">
      <section className="url-identity">
        <div>
          <span className="url-host">{new URL(dashboard.url.normalized).host}</span>
          <strong>{dashboard.url.normalized}</strong>
          <span>
            {dashboard.url.isHome ? "Vista global del sitio" : "Landing page"}
            {" · "}
            Actualizado{" "}
            {new Intl.DateTimeFormat("es-ES", {
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(dashboard.generatedAt))}
          </span>
        </div>
        <button className="secondary-button" onClick={onRefresh}>
          <RefreshCw size={15} />
          Actualizar fuentes
        </button>
      </section>

      {dashboard.warnings.length ? (
        <div className="warning-strip">
          {dashboard.warnings.map((warning) => (
            <span key={warning}>{warning}</span>
          ))}
        </div>
      ) : null}

      <section className="metric-grid">
        <MetricCard
          label="URL Health"
          metric={dashboard.summary.healthScore}
          icon={ShieldCheck}
          tone="green"
        />
        <MetricCard
          label="Organic clicks"
          metric={dashboard.summary.organicClicks}
          icon={MousePointerClick}
          tone="blue"
        />
        <MetricCard
          label="Paid clicks"
          metric={dashboard.summary.paidClicks}
          icon={CircleDollarSign}
          tone="amber"
        />
        <MetricCard
          label="Sessions"
          metric={dashboard.summary.sessions}
          icon={Users}
          tone="ink"
        />
        <MetricCard
          label="Conversions"
          metric={dashboard.summary.conversions}
          icon={Target}
          tone="red"
        />
        <MetricCard
          label="Performance"
          metric={dashboard.summary.performance}
          icon={Gauge}
          tone="green"
        />
      </section>

      {activeView === "paid" ? (
        <section className="panel full-panel">
          <PanelHeading
            title="Paid Search"
            meta="Google Ads y Microsoft Advertising"
          />
          <EmptyState
            variant="source"
            title="Falta completar OAuth de las plataformas Ads"
            body="Las cuentas y developer tokens están identificados, pero todavía faltan refresh tokens válidos para consultar campañas por landing."
          />
        </section>
      ) : null}

      {activeView !== "paid" ? (
        <section className="analysis-grid">
          <article className="panel chart-panel">
            <PanelHeading
              title="Calidad de la landing"
              meta={`${dashboard.history.length} snapshots`}
            />
            {dashboard.history.length ? (
              <Suspense fallback={<div className="chart-loading" />}>
                <PerformanceChart history={dashboard.history} />
              </Suspense>
            ) : (
              <EmptyState
                title="Aún no hay histórico"
                body="Los siguientes análisis construirán la serie temporal."
              />
            )}
          </article>

          <article className="panel score-panel">
            <PanelHeading title="Lighthouse" meta="Mobile" />
            <div className="score-grid">
              <ScoreRing label="Performance" value={scores?.performance ?? null} />
              <ScoreRing
                label="Accessibility"
                value={scores?.accessibility ?? null}
              />
              <ScoreRing
                label="Best practices"
                value={scores?.bestPractices ?? null}
              />
              <ScoreRing label="SEO" value={scores?.seo ?? null} />
            </div>
            <div className="vital-list">
              <Vital
                label="LCP"
                value={formatMilliseconds(dashboard.pageSpeed?.webVitals.lcpMs)}
              />
              <Vital
                label="INP"
                value={formatMilliseconds(dashboard.pageSpeed?.webVitals.inpMs)}
              />
              <Vital
                label="CLS"
                value={
                  dashboard.pageSpeed?.webVitals.cls === null ||
                  dashboard.pageSpeed?.webVitals.cls === undefined
                    ? "—"
                    : dashboard.pageSpeed.webVitals.cls.toFixed(3)
                }
              />
              <Vital
                label="TTFB"
                value={formatMilliseconds(dashboard.pageSpeed?.webVitals.ttfbMs)}
              />
            </div>
          </article>

          <article className="panel technical-panel">
            <PanelHeading title="Technical SEO" meta="Live URL" />
            <div className="technical-table">
              <TechnicalRow
                label="HTTP status"
                value={dashboard.technical?.statusCode?.toString() || "—"}
                good={
                  !!dashboard.technical?.statusCode &&
                  dashboard.technical.statusCode < 400
                }
              />
              <TechnicalRow
                label="Canonical"
                value={dashboard.technical?.canonical || "No detectada"}
                good={!!dashboard.technical?.canonical}
              />
              <TechnicalRow
                label="Robots"
                value={dashboard.technical?.robots || "Sin restricciones"}
                good={
                  !dashboard.technical?.robots?.toLowerCase().includes("noindex")
                }
              />
              <TechnicalRow
                label="Schema"
                value={
                  dashboard.technical?.schemaTypes.length
                    ? dashboard.technical.schemaTypes.join(", ")
                    : "No detectado"
                }
                good={
                  !!dashboard.technical?.schemaTypes.length &&
                  !dashboard.technical.schemaIssues.length
                }
              />
              <TechnicalRow
                label="Response time"
                value={
                  dashboard.technical?.responseTimeMs
                    ? `${dashboard.technical.responseTimeMs} ms`
                    : "—"
                }
                good={
                  !!dashboard.technical?.responseTimeMs &&
                  dashboard.technical.responseTimeMs < 1000
                }
              />
            </div>
          </article>

          <article className="panel query-panel">
            <PanelHeading
              title="Organic queries"
              meta={dashboard.bing ? "Bing Webmaster" : "Sin datos"}
            />
            {visibleQueries.length ? (
              <div className="query-table-wrap">
                <table className="query-table">
                  <thead>
                    <tr>
                      <th>Query</th>
                      <th>Clicks</th>
                      <th>Impr.</th>
                      <th>CTR</th>
                      <th>Pos.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleQueries.map((row) => (
                      <tr key={`${row.source}-${row.query}`}>
                        <td>{row.query}</td>
                        <td>{formatNumber(row.clicks)}</td>
                        <td>{formatNumber(row.impressions)}</td>
                        <td>
                          {row.ctr === null ? "—" : `${(row.ctr * 100).toFixed(1)}%`}
                        </td>
                        <td>{row.position?.toFixed(1) || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                variant="source"
                title="No hay queries disponibles"
                body="Bing puede no tener datos suficientes para esta URL o rango."
              />
            )}
          </article>

          <article className="panel opportunities-panel">
            <PanelHeading title="Priority actions" meta="PageSpeed" />
            <div className="opportunity-list">
              {dashboard.pageSpeed?.opportunities.length ? (
                dashboard.pageSpeed.opportunities.slice(0, 5).map((item, index) => (
                  <div className="opportunity-row" key={item.title}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <strong>{item.title}</strong>
                      <small>
                        {item.displayValue ||
                          (item.savingsMs
                            ? `Ahorro estimado ${Math.round(item.savingsMs)} ms`
                            : "Revisar oportunidad")}
                      </small>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-actions">
                  <Sparkles size={18} />
                  No hay oportunidades críticas en el último análisis.
                </div>
              )}
            </div>
          </article>
        </section>
      ) : null}
    </div>
  );
}

function EcosystemHeading({ title, meta }: { title: string; meta: string }) {
  return (
    <div className="ecosystem-heading">
      <div>
        <span>Microsoft</span>
        <strong>{title}</strong>
      </div>
      <small>{meta}</small>
    </div>
  );
}

function PanelHeading({ title, meta }: { title: string; meta: string }) {
  return (
    <div className="panel-heading">
      <div>
        <strong>{title}</strong>
        <span>{meta}</span>
      </div>
      <BarChart3 size={17} />
    </div>
  );
}

function TechnicalRow({
  label,
  value,
  good,
}: {
  label: string;
  value: string;
  good: boolean;
}) {
  return (
    <div className="technical-row">
      <span>{label}</span>
      <strong title={value}>{value}</strong>
      <i className={good ? "good" : "warning"} />
    </div>
  );
}

function Vital({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatMilliseconds(value: number | null | undefined) {
  return value === null || value === undefined ? "—" : `${Math.round(value)} ms`;
}

function formatNumber(value: number | null, locale = "es-ES") {
  return value === null
    ? "—"
    : new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

function formatCurrency(value: number | null | undefined, locale = "es-ES") {
  return value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(value);
}

function formatPercent(value: number | null | undefined) {
  return value === null || value === undefined
    ? "—"
    : `${(value * 100).toFixed(1)}%`;
}

function formatDateTime(value: string | null | undefined, locale = "es-ES") {
  if (!value) return "—";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
  }).format(new Date(value));
}

function formatCompareLabel(dashboard: DashboardResponse, text?: I18nText) {
  const compare = dashboard.dateRanges.compare;
  return compare
    ? text?.date.compareModes[compare.mode] || "Comparación"
    : text?.dashboard.noComparison || "sin comparativa";
}

function formatBytes(value: number | null | undefined) {
  if (value === null || value === undefined) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function DashboardSkeleton() {
  return (
    <div className="dashboard-skeleton">
      <div className="skeleton-line wide" />
      <div className="skeleton-grid">
        {Array.from({ length: 6 }).map((_, index) => (
          <div className="skeleton-card" key={index} />
        ))}
      </div>
      <div className="skeleton-panels">
        <div />
        <div />
        <div />
      </div>
    </div>
  );
}
